import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { BellRing, Camera, FilePlus2, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { api, fmtApiError, fmtFecha } from "@/lib/api";
import VisorArchivo from "@/components/VisorArchivo";
import { Avatar, Badge, Card, Modal, PageHeader, TIPO_ALERTA, btnPrim, btnSec, inputCls } from "@/components/rrhh/ui";

const TABS = [
  { id: "abierta", label: "Abiertas" },
  { id: "vista", label: "Vistas" },
  { id: "resuelta", label: "Resueltas" },
  { id: "todas", label: "Historial completo" },
];

const ESTADO_COLOR = { abierta: "rojo", vista: "amarillo", resuelta: "verde" };

export default function AlertasPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState("abierta");
  const [tipo, setTipo] = useState("");
  const [localId, setLocalId] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [fotoVer, setFotoVer] = useState(null);
  const [desbloquear, setDesbloquear] = useState(null); // alerta pin_bloqueado
  const [pinNuevo, setPinNuevo] = useState("");

  const { data: locales } = useQuery({ queryKey: ["locales"], queryFn: async () => (await api.get("/locales")).data });
  const { data: alertas, isLoading } = useQuery({
    queryKey: ["alertas", tab, tipo, localId, desde, hasta],
    queryFn: async () => (await api.get("/rrhh/alertas", {
      params: {
        estado: tab, tipo: tipo || undefined, local_id: localId || undefined,
        fecha_desde: desde || undefined, fecha_hasta: hasta || undefined,
      },
    })).data,
    refetchInterval: 5000,
  });

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["alertas"] });
    qc.invalidateQueries({ queryKey: ["alertas-resumen"] });
    qc.invalidateQueries({ queryKey: ["empleados"] });
  };

  const mutEstado = useMutation({
    mutationFn: async ({ id, estado }) => api.post(`/rrhh/alertas/${id}/estado`, { estado }),
    onSuccess: () => invalidar(),
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const mutDesbloquear = useMutation({
    mutationFn: async ({ empleadoId, pin }) => api.post(`/rrhh/empleados/${empleadoId}/desbloquear-pin`, { pin_nuevo: pin || null }),
    onSuccess: () => {
      invalidar();
      setDesbloquear(null);
      setPinNuevo("");
      toast.success("PIN desbloqueado — la alerta quedó resuelta");
    },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const verFoto = async (a) => {
    try {
      const { data } = await api.get("/rrhh/jornada", {
        params: { empleado_id: a.empleado_id, fecha: a.fecha_hora.slice(0, 10) },
      });
      const conFoto = data.fichadas.find((f) => f.foto_url);
      if (conFoto) {
        setFotoVer(conFoto.foto_url);
        api.post("/rrhh/registrar-vista", { que: "foto de la fichada", referencia: conFoto.foto_url, empleado_id: a.empleado_id }).catch(() => {});
      } else {
        toast.info("Ese día no hay fichada con foto");
      }
    } catch (e) {
      toast.error(fmtApiError(e));
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-alertas">
      <PageHeader
        titulo="Alertas"
        subtitulo="Lo que hay que revisar: tardanzas, faltas, PIN bloqueados y comprobantes que llegaron."
        testid="header-alertas"
      />

      <Card testid="card-filtros-alertas" className="mb-5" bodyCls="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl border border-white/10 bg-[#0B0F14] p-1" data-testid="tabs-estado">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-colors ${tab === t.id ? "bg-[#2DD4BF] text-black" : "text-gray-400 hover:text-white"}`}
                data-testid={`tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={inputCls} data-testid="filtro-tipo">
            <option value="">Todos los tipos</option>
            {Object.entries(TIPO_ALERTA).map(([id, t]) => <option key={id} value={id}>{t.label}</option>)}
          </select>
          <select value={localId} onChange={(e) => setLocalId(e.target.value)} className={inputCls} data-testid="filtro-local-alertas">
            <option value="">Todos los locales</option>
            {locales?.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
          </select>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className={inputCls} data-testid="filtro-desde" />
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className={inputCls} data-testid="filtro-hasta" />
        </div>
      </Card>

      <Card testid="card-alertas-lista" bodyCls="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-5 py-3 font-semibold">Tipo</th>
                <th className="px-4 py-3 font-semibold">Detalle</th>
                <th className="px-4 py-3 font-semibold">Empleado</th>
                <th className="px-4 py-3 font-semibold">Local</th>
                <th className="px-4 py-3 font-semibold">Fecha y hora</th>
                <th className="px-4 py-3 font-semibold">Estado</th>
                <th className="px-5 py-3 font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-500">Cargando…</td></tr>}
              {alertas?.map((a) => (
                <tr key={a.id} className="border-b border-white/5 last:border-0" data-testid={`alerta-${a.id.slice(0, 8)}`}>
                  <td className="px-5 py-3">
                    <Badge color={TIPO_ALERTA[a.tipo]?.color || "gris"}>{TIPO_ALERTA[a.tipo]?.label || a.tipo}</Badge>
                  </td>
                  <td className="max-w-64 px-4 py-3 text-[13px] text-gray-400">{a.detalle}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/rrhh/empleados/${a.empleado_id}?fecha=${a.fecha_hora.slice(0, 10)}`)}
                      className="flex items-center gap-2.5 text-left"
                      data-testid={`alerta-ficha-${a.id.slice(0, 8)}`}
                    >
                      <Avatar iniciales={`${a.empleado_nombre.split(",")[1]?.trim()[0] || ""}${a.empleado_nombre.split(",")[0][0]}`} size="sm" />
                      <span className="leading-tight">
                        <span className="block text-sm font-medium text-[#2DD4BF] hover:underline">{a.empleado_nombre}</span>
                        <span className="block font-mono-num text-[11px] text-gray-500">Legajo {a.legajo}</span>
                      </span>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-300">{a.local_nombre}</td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono-num text-xs text-gray-400">
                    {fmtFecha(a.fecha_hora)} {a.fecha_hora.slice(11, 16)}
                  </td>
                  <td className="px-4 py-3"><Badge color={ESTADO_COLOR[a.estado]}>{a.estado}</Badge></td>
                  <td className="px-5 py-3">
                    <span className="flex flex-wrap gap-1.5">
                      {a.tipo === "pin_bloqueado" && a.estado !== "resuelta" && (
                        <button className={`${btnSec} !px-2.5 !py-1 text-xs`} onClick={() => setDesbloquear(a)} data-testid="accion-desbloquear">
                          <LockOpen size={13} className="mr-1 inline" /> Desbloquear PIN
                        </button>
                      )}
                      {a.tipo === "tardanza" && (
                        <button className={`${btnSec} !px-2.5 !py-1 text-xs`} onClick={() => verFoto(a)} data-testid="accion-ver-foto">
                          <Camera size={13} className="mr-1 inline" /> Ver foto
                        </button>
                      )}
                      {(a.tipo === "retiro_anticipado" || a.tipo === "ausencia") && a.estado !== "resuelta" && (
                        <button
                          className={`${btnSec} !px-2.5 !py-1 text-xs`}
                          onClick={() => navigate(`/rrhh/justificativos?cargar=${a.empleado_id}`)}
                          data-testid="accion-pedir-justificativo"
                        >
                          <FilePlus2 size={13} className="mr-1 inline" /> Pedir justificativo
                        </button>
                      )}
                      {a.tipo === "justificativo_pendiente" && (
                        <button className={`${btnSec} !px-2.5 !py-1 text-xs`} onClick={() => navigate("/rrhh/justificativos")} data-testid="accion-revisar">
                          Revisar
                        </button>
                      )}
                      {a.estado === "abierta" && (
                        <button className={`${btnSec} !px-2.5 !py-1 text-xs`} onClick={() => mutEstado.mutate({ id: a.id, estado: "vista" })} data-testid="accion-marcar-vista">
                          Marcar vista
                        </button>
                      )}
                      {a.estado === "vista" && (
                        <button className={`${btnSec} !px-2.5 !py-1 text-xs`} onClick={() => mutEstado.mutate({ id: a.id, estado: "resuelta" })} data-testid="accion-resolver">
                          Resolver
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
              {alertas?.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center">
                    <BellRing size={26} className="mx-auto text-gray-600" />
                    <p className="mt-3 text-sm font-semibold text-gray-300">No hay alertas con estos filtros</p>
                    <p className="text-xs text-gray-500">Cuando pase algo que haya que revisar, aparece acá.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {desbloquear && (
        <Modal titulo={`Desbloquear PIN — ${desbloquear.empleado_nombre}`} onClose={() => { setDesbloquear(null); setPinNuevo(""); }} testid="modal-desbloquear">
          <p className="mb-4 text-sm text-gray-400">
            El PIN queda habilitado al instante y la alerta se marca resuelta. Opcionalmente asignale un PIN nuevo de 4 dígitos.
          </p>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">PIN nuevo (opcional)</label>
          <input
            value={pinNuevo}
            onChange={(e) => setPinNuevo(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="Dejar vacío para mantener el actual"
            className={`${inputCls} w-full font-mono-num tracking-[0.3em]`}
            data-testid="input-pin-nuevo"
          />
          <div className="mt-5 flex justify-end gap-2">
            <button className={btnSec} onClick={() => { setDesbloquear(null); setPinNuevo(""); }}>Cancelar</button>
            <button
              className={btnPrim}
              disabled={mutDesbloquear.isPending || (pinNuevo.length > 0 && pinNuevo.length < 4)}
              onClick={() => mutDesbloquear.mutate({ empleadoId: desbloquear.empleado_id, pin: pinNuevo })}
              data-testid="confirmar-desbloquear"
            >
              Desbloquear
            </button>
          </div>
        </Modal>
      )}

      {fotoVer && <VisorArchivo url={fotoVer} onClose={() => setFotoVer(null)} titulo="Foto de la fichada" />}
    </motion.div>
  );
}
